import { projectDns } from '@/environment';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import {
    PolotnoStoreActivePageKeys,
    getPageProperty,
    useCurrentProjectId,
    usePolotnoActivePageState,
    useSetPolotnoActivePageState,
} from '@/pages/AdvancedEditor/hooks';
import useGetProject from '@/pages/AdvancedEditor/hooks/useGetProject';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { useAnalytics } from '@/services/analytics';
import { formatS3Url } from '@/utils/s3';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import AudioPlayer from './AudioPlayer';
import { RecordAudioControl } from './controls/RecordAudioControl';
import UploadAudioControl from './controls/UploadAudioControl';
const Container = styled.div`
    display: flex;
    margin: 16px;
    flex-direction: column;

    .audio-player {
        padding: 16px;

        > div {
            width: 100%;
        }
    }
`;

interface Props {
    stateManage: {
        recordUploading: {
            value: boolean;
            set: (state: boolean) => void;
        };
        isRecording: {
            value: boolean;
            set: (state: boolean) => void;
        };
        uploadLoading: {
            value: boolean;
            set: (state: boolean) => void;
        };
    };
    uploadAudio: (file: File) => Promise<void>;
    handleComplete: () => void;
    updateSceneAudio: (audioData: AudioData | null) => void;
}

export const AudioSection = observer(({ stateManage, uploadAudio, handleComplete, updateSceneAudio }: Props) => {
    const { store } = useAdvancedEditorContext();
    const mixpanel = useAnalytics();

    const [uploadedAudio] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.uploadedAudio);
    const setSceneDurationCalculated = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.sceneDurationCalculated);

    const projectId = useCurrentProjectId();
    const { data: project } = useGetProject(projectId);

    const audioUrl = useMemo(() => {
        const sceneAudioUrl = uploadedAudio?.listenUrl;

        const url = sceneAudioUrl ? formatS3Url(projectDns, sceneAudioUrl) : undefined;
        return url;
    }, [uploadedAudio, project?.audio_url]);

    const audioData: AudioData = {
        data: null,
        name: audioUrl?.split('/').pop(),
        duration: uploadedAudio?.duration ?? 0,
        listenUrl: audioUrl && audioUrl + `?cache_busting=${getPageProperty(store.activePage, PolotnoStoreActivePageKeys.ttsCacheVersion)}`,
    };

    const onDelete = () => {
        mixpanel?.track('delete-audio', { event: 'click' });

        updateSceneAudio(null);

        setSceneDurationCalculated(false);
        stateManage.uploadLoading.set(false);
        stateManage.recordUploading.set(false);
    };

    return (
        <Container>
            {!!audioData.listenUrl && (
                <>
                    <AudioPlayer audioData={audioData} onDelete={onDelete} className="audio-player" />
                </>
            )}
            {!audioData.listenUrl && (
                <>
                    {!stateManage.uploadLoading.value && store.activePage && (
                        <RecordAudioControl
                            setIsUploading={stateManage.recordUploading.set}
                            isUploading={stateManage.recordUploading.value}
                            setIsRecording={stateManage.isRecording.set}
                            onRecordComplete={handleComplete}
                            sceneId={store.activePage.id}
                            updateSceneAudio={updateSceneAudio}
                        />
                    )}

                    {!(stateManage.isRecording.value || stateManage.recordUploading.value) && (
                        <UploadAudioControl
                            accept={{ 'audio/*': [], 'video/*': [] }}
                            onInputChange={uploadAudio}
                            setIsUploading={stateManage.uploadLoading.set}
                            isUploading={stateManage.uploadLoading.value}
                            onUploadComplete={handleComplete}
                        />
                    )}
                </>
            )}
        </Container>
    );
});
